/**
 * Parsing broker symbols into the currencies they trade.
 *
 * Broker symbol names are not standardised. The same instrument appears as
 * XAUUSD, XAUUSD.p, XAUUSD.pro, XAUUSDm or GOLD depending on the broker and the
 * account type, so anything that reasons about the underlying currencies has to
 * normalise first rather than slicing the raw string.
 */
/** Metals quoted like currencies under an X-prefixed ISO 4217 code. */
export const METALS = {
    XAU: 'Gold',
    XAG: 'Silver',
    XPT: 'Platinum',
    XPD: 'Palladium',
};
/**
 * Names for the codes a retail feed actually carries.
 *
 * Not exhaustive by design — an unknown code falls back to its own letters,
 * which is more useful than a wrong guess.
 */
export const CURRENCY_NAMES = {
    USD: 'US Dollar',
    EUR: 'Euro',
    GBP: 'British Pound',
    JPY: 'Japanese Yen',
    CHF: 'Swiss Franc',
    AUD: 'Australian Dollar',
    NZD: 'New Zealand Dollar',
    CAD: 'Canadian Dollar',
    CNH: 'Chinese Yuan (offshore)',
    CNY: 'Chinese Yuan',
    SEK: 'Swedish Krona',
    NOK: 'Norwegian Krone',
    DKK: 'Danish Krone',
    PLN: 'Polish Zloty',
    CZK: 'Czech Koruna',
    HUF: 'Hungarian Forint',
    TRY: 'Turkish Lira',
    ZAR: 'South African Rand',
    MXN: 'Mexican Peso',
    SGD: 'Singapore Dollar',
    HKD: 'Hong Kong Dollar',
    ...METALS,
};
/** Well-known aliases brokers use instead of the ISO pair. */
const ALIASES = {
    GOLD: { base: 'XAU', quote: 'USD' },
    SILVER: { base: 'XAG', quote: 'USD' },
    GOLDUSD: { base: 'XAU', quote: 'USD' },
};
/**
 * Split a broker symbol into base and quote.
 *
 * The suffix is stripped first, then the remaining six characters are split down
 * the middle. Six is the near-universal shape of a spot FX or metals symbol; a
 * name that does not fit is returned unrecognised rather than mangled, so an
 * index or a CFD shows its own name instead of two invented currencies.
 */
export function parsePairSymbol(symbol) {
    const raw = symbol.trim().toUpperCase();
    // Strip a dotted suffix (XAUUSD.p) or trailing lowercase marker (XAUUSDm).
    const dot = raw.indexOf('.');
    let core = dot === -1 ? raw : raw.slice(0, dot);
    let suffix = dot === -1 ? '' : raw.slice(dot);
    if (suffix === '' && core.length > 6) {
        const trailing = core.slice(6);
        // Only treat it as decoration when what remains is a plausible pair.
        if (/^[A-Z]{1,4}$/.test(trailing) && !ALIASES[core]) {
            suffix = symbol.trim().slice(6);
            core = core.slice(0, 6);
        }
    }
    const alias = ALIASES[core];
    if (alias) {
        return {
            base: alias.base,
            quote: alias.quote,
            suffix,
            isMetal: alias.base in METALS,
            recognised: true,
        };
    }
    if (core.length === 6 && /^[A-Z]{6}$/.test(core)) {
        const base = core.slice(0, 3);
        const quote = core.slice(3);
        return { base, quote, suffix, isMetal: base in METALS, recognised: true };
    }
    return { base: raw, quote: '', suffix: '', isMetal: false, recognised: false };
}
/** Human label, e.g. "Gold / US Dollar". */
export function describePair(symbol) {
    const p = parsePairSymbol(symbol);
    if (!p.recognised)
        return symbol;
    const base = CURRENCY_NAMES[p.base] ?? p.base;
    const quote = CURRENCY_NAMES[p.quote] ?? p.quote;
    return `${base} / ${quote}`;
}
//# sourceMappingURL=pair.js.map